import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Trash2, Mail, ChevronDown } from 'lucide-react'
import { cn, Button } from '@personic/shared'
import type { TeamInvite } from '@/stores/onboardingStore'

interface TeamInviteFormProps {
  invites: TeamInvite[]
  onAdd: (invite: TeamInvite) => void
  onRemove: (index: number) => void
  onUpdate: (index: number, invite: Partial<TeamInvite>) => void
  maxInvites?: number
  className?: string
}

export function TeamInviteForm({
  invites,
  onAdd,
  onRemove,
  onUpdate,
  maxInvites = 5,
  className,
}: TeamInviteFormProps) {
  const canAddMore = invites.length < maxInvites

  const handleAddInvite = () => {
    if (canAddMore) {
      onAdd({ email: '', role: 'member' })
    }
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  return (
    <div className={cn('space-y-4', className)}>
      <AnimatePresence mode="popLayout">
        {invites.map((invite, index) => {
          const isValidEmail = invite.email === '' || validateEmail(invite.email)

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="flex items-start gap-3"
            >
              {/* Email Input */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-400">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    value={invite.email}
                    onChange={(e) => onUpdate(index, { email: e.target.value })}
                    placeholder="colleague@company.com"
                    className={cn(
                      'w-full pl-12 pr-4 py-3 border rounded-xl bg-white font-body text-midnight-900',
                      'placeholder:text-warm-400',
                      'focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none',
                      'transition-all duration-200',
                      !isValidEmail
                        ? 'border-red-500 focus:ring-red-400'
                        : 'border-warm-300'
                    )}
                  />
                </div>
                {!isValidEmail && (
                  <p className="mt-1 text-sm text-red-600">
                    Please enter a valid email address
                  </p>
                )}
              </div>

              {/* Role Selector */}
              <div className="relative w-32">
                <select
                  value={invite.role}
                  onChange={(e) =>
                    onUpdate(index, { role: e.target.value as 'admin' | 'member' })
                  }
                  className={cn(
                    'w-full px-4 py-3 border rounded-xl bg-white font-body text-midnight-900',
                    'focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none',
                    'transition-all duration-200 cursor-pointer appearance-none',
                    'border-warm-300'
                  )}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-400 pointer-events-none" />
              </div>

              {/* Remove Button */}
              <motion.button
                type="button"
                onClick={() => onRemove(index)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={cn(
                  'p-3 rounded-xl text-warm-400 hover:text-red-500',
                  'hover:bg-red-50 transition-colors duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-red-400'
                )}
              >
                <Trash2 size={18} />
              </motion.button>
            </motion.div>
          )
        })}
      </AnimatePresence>

      {/* Add Invite Button */}
      {canAddMore && (
        <Button
          type="button"
          variant="outline"
          onClick={handleAddInvite}
          leftIcon={<Plus size={18} />}
          className="w-full border-dashed"
        >
          Add Team Member
        </Button>
      )}

      {/* Max Reached Message */}
      {!canAddMore && (
        <p className="text-center text-sm text-warm-500">
          Maximum of {maxInvites} invites reached
        </p>
      )}

      {/* Help Text */}
      {invites.length > 0 && (
        <div className="mt-4 p-4 bg-midnight-50 rounded-xl">
          <p className="text-sm text-midnight-600">
            <strong className="font-heading">Note:</strong> Invitations will be
            sent once your account is approved. Team members will receive an
            email to join your organization.
          </p>
        </div>
      )}
    </div>
  )
}
